import { useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Loader2, KeyRound, Lock, Undo2 } from "lucide-react";
import { toast } from "sonner";

const fmtIt = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join("/") : "—");
const yesterdayIso = () => new Date(Date.now() - 86400000).toISOString().slice(0, 10);

/** Ceased employee: shows until when the portal login is allowed and lets the office
 *  extend / re-enable (custom date), block now, or restore the standard rule. */
export const PortalAccessControl = ({ employee, onUpdated }) => {
    const [editing, setEditing] = useState(false);
    const [date, setDate] = useState("");
    const [saving, setSaving] = useState(false);
    const pa = employee?.portal_access;
    if (!employee?.data_cessazione || !pa) return null;

    const save = async (value, okMsg) => {
        setSaving(true);
        try {
            const { data } = await api.put(`/employees/${employee.id}`, { portal_access_until: value });
            toast.success(okMsg);
            setEditing(false);
            onUpdated && onUpdated(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const blocked = pa.status === "blocked";
    const hasCreds = !!employee.can_login;
    return (
        <div className="flex items-start justify-between gap-4" data-testid="portal-access-row">
            <dt className="text-xs uppercase tracking-wide text-slate-500">Accesso portale</dt>
            <dd className="flex flex-col items-end gap-1.5 text-sm">
                <div className="flex items-center gap-2">
                    <Badge
                        variant="outline"
                        className={`text-[10px] uppercase tracking-wide ${blocked ? "border-red-300 bg-red-50 text-red-700" : "border-amber-300 bg-amber-50 text-amber-700"}`}
                        data-testid="portal-access-badge"
                    >
                        {blocked ? "Bloccato" : "In scadenza"}
                    </Badge>
                    <span className="text-slate-700" data-testid="portal-access-text">
                        {blocked ? `bloccato dal ${fmtIt(nextDay(pa.until))}` : `fino al ${fmtIt(pa.until)}`}
                        <span className="ml-1 text-[11px] text-slate-400">{pa.override ? "(data impostata dall'ufficio)" : "(regola: 31/03 dell'anno dopo la cessazione)"}</span>
                    </span>
                </div>
                {!hasCreds && <span className="text-[11px] text-slate-400">Nessuna credenziale attiva: il dipendente non accede comunque.</span>}
                {editing ? (
                    <div className="flex items-center gap-1.5">
                        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-8 w-40 text-xs" data-testid="portal-access-date-input" />
                        <Button size="sm" className="h-8 bg-[#376DB3] text-white hover:bg-[#376DB3]/90" disabled={!date || saving} onClick={() => save(date, `Accesso al portale consentito fino al ${fmtIt(date)}`)} data-testid="portal-access-save-btn">
                            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Salva"}
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditing(false)} data-testid="portal-access-cancel-btn">Annulla</Button>
                    </div>
                ) : (
                    <div className="flex flex-wrap justify-end gap-1.5">
                        <Button size="sm" variant="outline" className="h-7 gap-1 border-emerald-300 text-xs text-emerald-700 hover:bg-emerald-50" onClick={() => { setDate(pa.until && !blocked ? pa.until : ""); setEditing(true); }} data-testid="portal-access-extend-btn">
                            <KeyRound className="h-3 w-3" /> {blocked ? "Riattiva accesso fino al…" : "Estendi fino al…"}
                        </Button>
                        {!blocked && (
                            <Button size="sm" variant="outline" className="h-7 gap-1 border-red-300 text-xs text-red-700 hover:bg-red-50" disabled={saving} onClick={() => save(yesterdayIso(), "Accesso al portale bloccato")} data-testid="portal-access-block-btn">
                                <Lock className="h-3 w-3" /> Blocca ora
                            </Button>
                        )}
                        {pa.override && (
                            <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs text-slate-600" disabled={saving} onClick={() => save("", "Ripristinata la regola standard (31/03 dell'anno successivo)")} data-testid="portal-access-reset-btn">
                                <Undo2 className="h-3 w-3" /> Regola standard
                            </Button>
                        )}
                    </div>
                )}
            </dd>
        </div>
    );
};

const nextDay = (iso) => {
    if (!iso) return null;
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
};
